import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#2A331A]">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" data-testid="auth-brand-link"><Logo tone="light" /></Link>
          <div>
            <div className="overline text-slate-300">Members-only</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight text-white max-w-md">
              A single coupon.<br /><span className="text-[#D4AF37]">A lifetime of small privileges.</span>
            </h2>
            <p className="mt-4 text-slate-300 text-sm max-w-md">Every Crown Coupon is a numbered pass, verified on the BNB Smart Chain. Welcome to a smaller, more thoughtful circle.</p>
          </div>
          <div className="text-xs text-slate-400">USDT BEP-20 · non-custodial · verified on-chain</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-[#132741]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6"><Link to="/"><Logo /></Link></div>
          <div className="overline mb-2">{subtitle}</div>
          <h1 className="font-serif text-4xl text-white">{title}</h1>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      nav(user.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Login failed"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell subtitle="Sign in" title="Welcome back.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label>Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="login-email" />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" data-testid="login-password" />
        </div>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="link-btn text-sm" data-testid="link-forgot">Forgot password?</Link>
        </div>
        <Button disabled={loading} type="submit" className="w-full bg-[#556B2F] hover:bg-[#2A331A] text-white h-11" data-testid="login-submit">
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-slate-300 text-center">
        Don't have an account? <Link to="/register" className="link-btn" data-testid="link-to-register">Create one</Link>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", password: "", confirm: "",
    referral_code: sp.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await register({
        full_name: form.full_name, email: form.email, mobile: form.mobile,
        password: form.password, referral_code: form.referral_code || null,
      });
      toast.success("Account created");
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Registration failed"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell subtitle="Create account" title="Join the Crown circle.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label>Full name</Label>
          <Input required value={form.full_name} onChange={set("full_name")} className="mt-1" data-testid="reg-fullname" />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" required value={form.email} onChange={set("email")} className="mt-1" data-testid="reg-email" />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input required value={form.mobile} onChange={set("mobile")} className="mt-1" data-testid="reg-mobile" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Password</Label>
            <Input type="password" required value={form.password} onChange={set("password")} className="mt-1" data-testid="reg-password" />
          </div>
          <div>
            <Label>Confirm</Label>
            <Input type="password" required value={form.confirm} onChange={set("confirm")} className="mt-1" data-testid="reg-confirm" />
          </div>
        </div>
        <div>
          <Label>Referral code (optional)</Label>
          <Input value={form.referral_code} onChange={set("referral_code")} className="mt-1" data-testid="reg-referral" />
        </div>
        <Button disabled={loading} type="submit" className="w-full bg-[#556B2F] hover:bg-[#2A331A] text-white h-11" data-testid="register-submit">
          {loading ? "Creating..." : "Create account"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-slate-300 text-center">
        Already registered? <Link to="/login" className="link-btn" data-testid="link-to-login">Sign in</Link>
      </div>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.dev_link) setDevLink(data.dev_link);
      toast.success("If the email exists, a reset link has been sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell subtitle="Password recovery" title="Reset your password.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <Label>Registered email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="forgot-email" />
        </div>
        <Button disabled={loading} type="submit" className="w-full bg-[#556B2F] text-white h-11" data-testid="forgot-submit">
          {loading ? "Sending..." : "Send reset link"}
        </Button>
        {devLink && (
          <div className="text-xs bg-amber-50 text-amber-900 border border-amber-200 rounded p-3 break-all">
            Dev link: <a href={devLink} className="underline">{devLink}</a>
          </div>
        )}
      </form>
      <div className="mt-6 text-sm text-slate-300 text-center">
        <Link to="/login" className="link-btn" data-testid="link-back-login">Back to sign in</Link>
      </div>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell subtitle="Choose new password" title="Set a new password.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label>New password</Label>
          <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="reset-password" />
        </div>
        <div>
          <Label>Confirm password</Label>
          <Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" data-testid="reset-confirm" />
        </div>
        <Button disabled={loading || !token} type="submit" className="w-full bg-[#556B2F] text-white h-11" data-testid="reset-submit">
          {loading ? "Updating..." : "Update password"}
        </Button>
        {!token && <div className="text-sm text-red-600">Missing token in URL.</div>}
      </form>
    </AuthShell>
  );
}
